import dynamic from "next/dynamic";
import { useCallback, useMemo, useState, useEffect } from "react";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { ICONS } from "@artha/icons";
import {
  Typography,
  JobCardPublic,
  JobCardSkeletonList,
  Pagination,
  Button,
} from "@artha/ui";
import {
  formatSalary,
  shouldHideSalaryBadge,
  normalizeUsernameParam,
  getAppliedFiltersCount,
  generateEarnCtaUrl,
  getMediaAssetUrl,
} from "@/lib/utils";
import { formatCompactNumber } from "@artha/utils";
import type {
  JobFiltersResponseDto,
  JobItemDto,
  JobsResponseDto,
  RecommendedJobsResponseDto,
} from "@artha/api-contracts";
import type { CreatorProfileForMyLink } from "@artha/types";
import { usePublicProfileJobs } from "@/modules/creator/hooks/usePublicProfileJobs";
import { useAudienceAnalytics } from "@/lib/analytics";
import {
  useInsightsRecommendedJobsQuery,
  usePublicRecommendedJobsQuery,
} from "@/modules/insights/services/insightsClient";
import { useSortedRecommendedJobs } from "@/modules/dashboard/hooks/useSortedRecommendedJobs";
import {
  getJobCountryForRestriction,
  getViewerGeo,
  type ViewerGeo,
} from "@/modules/geolocation/services/geolocationRestriction";
import {
  interleaveJobAlertCards,
  JobAlertSubscriptionCard,
  usePublicJobAlertSubscription,
} from "@/modules/job-alerts";
import {
  isResumePersonalizationUiEnabled,
  ResumePersonalizationSheet,
  ResumePersonalizationTrigger,
  useCandidatePersonalization,
} from "@/modules/candidate-personalization";

const JobDetailsDrawer = dynamic(
  () =>
    import("@/components/jobs/JobDetailsDrawer").then(
      (mod) => mod.JobDetailsDrawer,
    ),
  { ssr: false },
);

const FiltersBottomSheet = dynamic(
  () => import("@artha/ui").then((mod) => mod.FiltersBottomSheet),
  { ssr: false },
);

type PublicJobFeedProps = {
  initialJobs?: JobsResponseDto;
  initialRecommendedJobs?: RecommendedJobsResponseDto | null;
  initialPublicRecommendedJobs?: JobsResponseDto | null;
  username: string;
  creator: CreatorProfileForMyLink;
  filters?: JobFiltersResponseDto | null;
  isAuthenticated: boolean | null;
  ctaColor: string;
  jobsTopRef: React.RefObject<HTMLDivElement | null>;
  isPreviewMode?: boolean;
  onEarnClick?: () => void;
};

type PublicFeedTab = "jobs" | "audience";

const normalizeLocationValue = (value?: string | null) =>
  value?.trim().toLowerCase() || "";

const matchesLocationValue = (
  jobValue?: string | null,
  viewerValue?: string,
) => {
  const normalizedJobValue = normalizeLocationValue(jobValue);
  const normalizedViewerValue = normalizeLocationValue(viewerValue);

  if (!normalizedJobValue || !normalizedViewerValue) {
    return false;
  }

  return (
    normalizedJobValue === normalizedViewerValue ||
    normalizedJobValue.includes(normalizedViewerValue) ||
    normalizedViewerValue.includes(normalizedJobValue)
  );
};

const sortJobsByViewerGeo = (
  jobs: JobItemDto[],
  viewerGeo: ViewerGeo | null,
) => {
  if (!viewerGeo) {
    return jobs;
  }

  const viewerCountryCode = viewerGeo.country?.code?.toLowerCase();

  const getGeoScore = (job: JobItemDto) => {
    if (viewerGeo.city && matchesLocationValue(job.city, viewerGeo.city)) {
      return 3;
    }
    if (viewerGeo.state && matchesLocationValue(job.state, viewerGeo.state)) {
      return 2;
    }
    const jobCountryCode =
      getJobCountryForRestriction(job)?.code?.toLowerCase();
    if (viewerCountryCode && jobCountryCode === viewerCountryCode) {
      return 1;
    }
    return 0;
  };

  return [...jobs].sort((a, b) => getGeoScore(b) - getGeoScore(a));
};

export function PublicJobFeed({
  username,
  creator,
  filters,
  isAuthenticated,
  ctaColor,
  jobsTopRef,
  isPreviewMode = false,
  onEarnClick,
}: PublicJobFeedProps) {
  const t = useTranslations("profile");
  const common = useTranslations("common");
  const router = useRouter();
  const { trackJobApply, trackJobFilterJob } = useAudienceAnalytics();

  const [selectedJob, setSelectedJob] = useState<JobItemDto | null>(null);
  const [activeTab, setActiveTab] = useState<PublicFeedTab>("jobs");
  const [viewerGeo, setViewerGeo] = useState<ViewerGeo | null>(null);
  const [isResumeSheetOpen, setIsResumeSheetOpen] = useState(false);
  const personalization = useCandidatePersonalization(creator.id);

  const {
    jobs,
    totalJobs,
    totalPages,
    isLoading,
    isFetching,
    isError,
    error,
    currentPage,
    setCurrentPage,
    pageSize,
    onPageSizeChange,
    isFiltersOpen,
    setIsFiltersOpen,
    appliedFilters,
    setAppliedFilters,
    draftFilters,
    setDraftFilters,
    filtersForSheet,
    handleFiltersReset,
    preferenceFilterState,
  } = usePublicProfileJobs({
    username,
    creator,
    filters,
    personalization: {
      isSessionLoading: personalization.isSessionLoading,
      externalId: personalization.externalId,
      isPersonalized: personalization.isPersonalized,
      candidatePreferences: personalization.candidatePreferences,
      jobsRefreshVersion: personalization.jobsRefreshVersion,
    },
  });
  // Wait until auth status is resolved (non-null) so exactly one of the two
  // recommended-jobs queries fires, instead of the public one firing first
  // and the creator one replacing it after the auth check completes.
  const shouldLoadAudienceJobs = isPreviewMode || isAuthenticated === true;
  const creatorRecommended = useInsightsRecommendedJobsQuery(
    { rangePreset: "90d" },
    {
      enabled: shouldLoadAudienceJobs,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );
  const publicRecommended = usePublicRecommendedJobsQuery(username, {
    enabled: !isPreviewMode && isAuthenticated === false,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const recommendedJobsResponse = shouldLoadAudienceJobs
    ? creatorRecommended.data
    : isAuthenticated === false
      ? publicRecommended.data
      : undefined;
  const isRecommendedJobsLoading = shouldLoadAudienceJobs
    ? creatorRecommended.isLoading
    : isAuthenticated === false
      ? publicRecommended.isLoading
      : true;

  const recommendedJobs = useSortedRecommendedJobs(
    recommendedJobsResponse?.items,
  );
  const showAudienceTab =
    !isRecommendedJobsLoading && recommendedJobs.length > 0;
  const showAudienceTabLoader = !showAudienceTab && isRecommendedJobsLoading;
  const appliedFiltersCount = useMemo(
    () => getAppliedFiltersCount(appliedFilters, preferenceFilterState),
    [appliedFilters, preferenceFilterState],
  );

  const normalizedUsername = useMemo(
    () => normalizeUsernameParam(username),
    [username],
  );
  const jobAlertSubscription = usePublicJobAlertSubscription(
    normalizedUsername,
    personalization.editableProfile?.email,
  );

  const earnHref = generateEarnCtaUrl(creator.referralInviteCode);

  const getJobHref = useCallback(
    (job: JobItemDto) =>
      job.slug
        ? `/@${encodeURIComponent(normalizedUsername)}/jobs/${encodeURIComponent(job.slug)}`
        : job.url || "#",
    [normalizedUsername],
  );

  const handleJobSelect = useCallback(
    (job: JobItemDto) => {
      const jobHref = getJobHref(job);
      if (job.slug && typeof window !== "undefined") {
        if (selectedJob) {
          window.history.replaceState(window.history.state, "", jobHref);
        } else {
          window.history.pushState(window.history.state, "", jobHref);
        }
      }
      setSelectedJob(job);
    },
    [getJobHref, selectedJob],
  );

  const handleJobDrawerClose = useCallback(() => {
    setSelectedJob(null);
    if (selectedJob?.slug && typeof window !== "undefined") {
      window.history.back();
    }
  }, [selectedJob]);

  useEffect(() => {
    const handlePopState = () => setSelectedJob(null);

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const handleSearchRedirect = () => {
    const normalized = normalizeUsernameParam(username);
    const handle = `@${encodeURIComponent(normalized)}`;
    router.push(`/${handle}/search?search=1`);
  };

  const applyPortalContext = useMemo(
    () => ({
      portalId: creator.id,
      clientName: creator.displayName || creator.username,
      domainName: creator.username,
    }),
    [creator.displayName, creator.id, creator.username],
  );
  const feedJobs = jobs;
  const sortedJobs = useMemo(
    () => sortJobsByViewerGeo(feedJobs, viewerGeo),
    [feedJobs, viewerGeo],
  );
  const visibleJobs = activeTab === "audience" ? recommendedJobs : sortedJobs;
  const interleavedJobs = useMemo(() => {
    if (jobAlertSubscription.externalId && !jobAlertSubscription.isModalOpen) {
      return visibleJobs.map((job, index) => ({
        type: "job" as const,
        key: `job-${index}`,
        job,
        index,
      }));
    }
    return interleaveJobAlertCards(visibleJobs);
  }, [
    visibleJobs,
    jobAlertSubscription.externalId,
    jobAlertSubscription.isModalOpen,
  ]);
  const hasJobs = visibleJobs.length > 0;
  const showJobSkeletons =
    activeTab === "audience"
      ? isRecommendedJobsLoading && !hasJobs
      : isLoading && !hasJobs;

  useEffect(() => {
    if (
      !showAudienceTab &&
      !showAudienceTabLoader &&
      activeTab === "audience"
    ) {
      setActiveTab("jobs");
    }
  }, [activeTab, showAudienceTab, showAudienceTabLoader]);

  useEffect(() => {
    let cancelled = false;

    void getViewerGeo().then((geo) => {
      if (!cancelled) {
        setViewerGeo(geo);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);
  const renderTabButton = (
    tab: PublicFeedTab,
    label: string,
    testId: string,
    disabled = false,
  ) => (
    <button
      type="button"
      data-testid={testId}
      onClick={() => {
        if (!disabled) {
          setActiveTab(tab);
        }
      }}
      disabled={disabled}
      className={
        activeTab === tab
          ? "min-w-0 flex-1 border-b-2 border-[#ED5AA9] px-4 py-3 text-center text-sm font-semibold text-[#ED5AA9] whitespace-nowrap"
          : `min-w-0 flex-1 border-b-2 border-transparent px-4 py-3 text-center text-sm font-semibold whitespace-nowrap ${
              disabled
                ? "cursor-wait text-[#98A2B3]"
                : "text-[#667085] transition-colors hover:text-[#344054]"
            }`
      }
    >
      {label}
    </button>
  );

  return (
    <>
      <section className="creator-jobs-section w-full space-y-2">
        <div
          ref={jobsTopRef}
          className="creator-jobs-header flex items-center gap-2"
        >
          {showAudienceTab || showAudienceTabLoader ? (
            <div className="mb-4 flex w-full overflow-x-auto overflow-y-hidden border-b border-[#EAECF0]">
              {renderTabButton(
                "jobs",
                `${t("jobsTitle")}${
                  typeof totalJobs === "number" && totalJobs > 0
                    ? ` (${formatCompactNumber(totalJobs)})`
                    : ""
                }`,
                "public-feed-tab-jobs",
              )}
              {renderTabButton(
                "audience",
                showAudienceTabLoader
                  ? "💕 By Audience"
                  : `💕 By Audience${recommendedJobs.length > 0 ? ` (${formatCompactNumber(recommendedJobs.length)})` : ""}`,
                "public-feed-tab-audience",
                showAudienceTabLoader,
              )}
            </div>
          ) : (
            <Typography
              variant="h2"
              className="text-[18px] font-semibold text-black leading-none whitespace-nowrap"
            >
              {t("jobsTitle")}
              {typeof totalJobs === "number" && totalJobs > 0
                ? ` (${formatCompactNumber(totalJobs)})`
                : ""}
            </Typography>
          )}
        </div>

        {showJobSkeletons && <JobCardSkeletonList count={3} />}

        {!showJobSkeletons &&
          interleavedJobs.map((item) =>
            item.type === "subscription-card" ? (
              <JobAlertSubscriptionCard
                key={item.key}
                cardId={item.key}
                controller={jobAlertSubscription}
                creatorName={creator?.displayName}
              />
            ) : (
              <a
                key={`${item.job.ref_id || item.job.id || item.job.url || "job"}-${item.index}`}
                href={getJobHref(item.job)}
                className="creator-job-card-link block w-full text-left"
                onClick={(event) => {
                  event.preventDefault();
                  handleJobSelect(item.job);
                }}
              >
                <JobCardPublic
                  jobTitle={item.job.title}
                  companyName={item.job.company}
                  companyLogo={getMediaAssetUrl(item.job.logo)}
                  location={item.job.location || ""}
                  city={item.job.city}
                  state={item.job.state}
                  country={item.job.country}
                  salary={formatSalary(item.job)}
                  experience=""
                  workMode={item.job.job_type ?? ""}
                  skills={
                    item.job.skills?.length
                      ? item.job.skills
                      : item.job.keywords
                  }
                  hideSalary={shouldHideSalaryBadge(item.job)}
                  cpaValue={isAuthenticated ? item.job.cpa_value : undefined}
                  cpcValue={isAuthenticated ? item.job.cpc_value : undefined}
                />
              </a>
            ),
          )}

        {activeTab === "jobs" && isError && (
          <div className="w-full flex justify-center">
            <Typography variant="lead" className="text-center text-destructive">
              {error?.message || t("unableToLoadJobs")}
            </Typography>
          </div>
        )}

        {!showJobSkeletons &&
          !(activeTab === "jobs" && isError) &&
          !hasJobs && (
            <div className="w-full flex justify-center">
              <Typography variant="lead" className="text-center text-[#475467]">
                {activeTab === "audience"
                  ? "No audience-matched jobs found yet."
                  : t("noJobsFound")}
              </Typography>
            </div>
          )}

        {activeTab === "jobs" && totalPages > 0 && (
          <div className="flex min-h-[44px] w-full justify-center pt-2">
            <Pagination
              currentPage={currentPage}
              pageSize={pageSize}
              onPageSizeChange={onPageSizeChange}
              pageSizeLabel={common("jobsPerPage")}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              disabled={isFetching}
              iconClassName="text-[#344054]"
            />
          </div>
        )}

        {!creator?.hidePoweredBy && (
          <Button
            asChild
            variant="outline"
            className="creator-earn-cta mt-2 w-full gap-2 rounded-[8px] border border-black bg-white px-3 py-5 text-[16px] font-medium shadow-[0px_4px_0px_0px_#000000]"
            style={{ color: ctaColor }}
          >
            <a href={earnHref} onClick={onEarnClick}>
              <ICONS.ARTHA_LINK_BADGE className="h-5 w-5" />
              {t("earnCta")}
            </a>
          </Button>
        )}
      </section>

      {!isPreviewMode && activeTab === "jobs" && (
        <div className="creator-floating-actions fixed bottom-6 left-1/2 z-40 w-[95%] max-w-[415px] -translate-x-1/2 px-4 pointer-events-none">
          <div className="w-full flex items-end gap-[10px] pointer-events-auto">
            <button
              type="button"
              onClick={handleSearchRedirect}
              className="creator-search-trigger flex flex-1 items-center gap-[10px] rounded-[8px] border border-black bg-white px-[15px] py-[10px] text-left shadow-[0px_4px_0px_0px_#000000] cursor-pointer"
            >
              <ICONS.SEARCH className="h-5 w-5 text-[#667085]" />
              <span className="flex-1 text-[14px] text-[#667085]">
                {t("searchPlaceholder")}
              </span>
            </button>
            <div className="flex shrink-0 flex-col items-center gap-2">
              {isResumePersonalizationUiEnabled ? (
                <ResumePersonalizationTrigger
                  isPersonalized={personalization.isPersonalized}
                  isSessionLoading={personalization.isSessionLoading}
                  isUploading={personalization.isUploading}
                  onClick={() => setIsResumeSheetOpen(true)}
                />
              ) : null}
              <Button
                variant="outline"
                size="icon"
                className="creator-filter-trigger relative h-10 w-10 rounded-[8px] border-black bg-white p-[10px] shadow-[0px_4px_0px_0px_#000000]"
                aria-label={t("filterJobsAria")}
                onClick={() => setIsFiltersOpen(true)}
              >
                <ICONS.FILTER2 className="h-5 w-5 text-[#101828]" />
                {appliedFiltersCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 flex h-[18px] w-[18px] items-center justify-center rounded-full border border-black bg-[#FF60B5] text-[10px] font-bold text-white shadow-[1px_1px_0px_0px_#000000]">
                    {appliedFiltersCount}
                  </span>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {selectedJob ? (
        <JobDetailsDrawer
          job={selectedJob}
          onClose={handleJobDrawerClose}
          onSelectJob={handleJobSelect}
          onApply={(job, clickAttemptId) => {
            if (activeTab === "audience") {
              trackJobApply(job, true, clickAttemptId);
            } else {
              trackJobApply(job, false, clickAttemptId);
            }
          }}
          portalContext={applyPortalContext}
          creator={creator}
          creatorName={creator.displayName || creator.username}
          creatorAvatarUrl={getMediaAssetUrl(creator.avatarUrl)}
          referralInviteCode={creator.referralInviteCode}
          hidePoweredBy={creator.hidePoweredBy}
        />
      ) : null}

      {isResumePersonalizationUiEnabled ? (
        <ResumePersonalizationSheet
          isOpen={isResumeSheetOpen}
          onClose={() => setIsResumeSheetOpen(false)}
          controller={personalization}
        />
      ) : null}

      {isFiltersOpen ? (
        <FiltersBottomSheet
          isOpen={isFiltersOpen}
          onClose={() => setIsFiltersOpen(false)}
          filters={filtersForSheet ?? undefined}
          value={draftFilters}
          onChange={setDraftFilters}
          onApply={(next) => {
            trackJobFilterJob(appliedFilters, next);
            setAppliedFilters(next);
          }}
          onReset={handleFiltersReset}
          showEmployerFilters={false}
        />
      ) : null}
    </>
  );
}
